import { Col, Image, Modal, Row, Spin } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChatContext } from "../../context/ChatContext";
import ShowContent from "../ShowContent/ShowContent";
import "./style.scss";
import { useImageLoader } from "../customHook/ImageLoader";
import noImage from "../../../src/assets/no-image.avif";
import MediaViewerModal from "../modal/MediaViewerModal";
import { useResourceViewer } from "../customHook/ResourceViewer";

const ImagePiece = ({
  resourceId,
  index,
  xsCol,
  mdCol,
  cssType,
  openModal,
  bg,
  overlay,
  content,
  isMention,
  styleImg,
  imagesToShow,
  styleLoadingDiv,
  imageData,
}) => {
  const { messageRef, wasAtBottomMessageRef } = useChatContext();

  const { imageUrl, loading, error } = useImageLoader(
    !imageData?.isUploading ? resourceId : null,
  );

  const scrollAfterImageLoad = useCallback(() => {
    const messageBox = messageRef.current;

    if (!messageBox) return;

    if (wasAtBottomMessageRef.current) {
    }
  }, [messageRef, wasAtBottomMessageRef]);

  useEffect(() => {
    scrollAfterImageLoad();
  }, [imageUrl, loading, scrollAfterImageLoad]);

  const renderImage = useMemo(() => {
    if (imageData?.isUploading && imageData?.pathUrl) {
      return (
        <img
          src={imageData.pathUrl}
          style={styleImg}
          alt="uploading"
          className="w-full h-full object-cover rounded-md"
        />
      );
    }
    if (loading && !imageData?.isUploading) {
      return (
        <div
          style={styleLoadingDiv?.() || {}}
          className="flex items-center justify-center min-h-[100px]"
        >
          <Spin />
        </div>
      );
    }

    if (error && !imageData?.isUploading) {
      return (
        <Image
          style={styleImg}
          className="bg-white"
          preview={false}
          src={noImage}
          alt="No Image"
        />
      );
    }

    if (imageUrl) {
      return <Image src={imageUrl} style={styleImg} preview={false} />;
    }

    return null;
  }, [imageUrl, loading, error, styleImg, styleLoadingDiv, imageData]);

  return (
    <Col
      xs={xsCol}
      md={mdCol}
      className={`border background ${cssType} flex ${
        imageUrl ? "flex-col" : "flex-row"
      } justify-center`}
    >
      <div className="flex flex-col mb-[2px] pointer max-w-[30vw] items-center justify-center">
        <div className="cursor-pointer" onClick={() => openModal(index)}>
          {renderImage}
        </div>

        {content?.length > 0 && (
          <ShowContent
            content={content}
            isMention={isMention}
            maxLength={300}
            hasImage={true}
          />
        )}
      </div>
      {overlay}
    </Col>
  );
};

const GridPhotos = ({
  images,
  countFrom,
  hideOverlay = true,
  bg,
  isSelf,
  content,
  isMention,
  createdAt,
  senderId,
}) => {
  const resourceViewer = useResourceViewer();
  const [height, setHeight] = useState("!h-[380px]");
  const [isImageLoaded, setIsImageLoaded] = useState(true);

  const openModal = (curentImageIndex) => {
    resourceViewer?.handleOpenViewer(
      {
        ...images[curentImageIndex],
        createdAt: createdAt,
        userId: senderId,
      },
      createdAt,
    );
  };

  const imagesToShow = useMemo(() => {
    if (countFrom && images?.length > countFrom) {
      return countFrom;
    } else {
      return images?.length;
    }
  }, [countFrom, images]);

  const loadedImage = () => {
    setHeight("!max-h-[380px]");
  };

  const handleImageError = () => {
    setIsImageLoaded(false);
  };

  const renderTwo = () => {
    const conditionalRender =
      [3, 4].includes(images?.length) ||
      (images?.length > +countFrom && [3, 4].includes(+countFrom));

    if (!isImageLoaded) {
      return <div>Loading...</div>;
    }

    return (
      <Row>
        <ImagePiece
          resourceId={
            conditionalRender
              ? images?.[1]?.previewImgResourceId || images?.[1]?.resourceId
              : images?.[0]?.previewImgResourceId || images?.[0]?.resourceId
          }
          index={conditionalRender ? 1 : 0}
          xsCol={12}
          mdCol={12}
          cssType={"height-two"}
          minHeightLoading={"min-h-[200px]"}
          openModal={openModal}
          height={height}
          styleImg={{
            minHeight: "5vw",
            maxHeight: "380px",
            objectFit: "cover",
            transform: "translateZ(0.1px)",
            objectPosition: "center",
          }}
          loadedImage={loadedImage}
          handleImageError={handleImageError}
          renderOverlay={renderOverlay}
          bg={bg}
          styleLoadingDiv={() =>
            calculateCssLoadingDiv({
              type: "height",
              imgWidth: conditionalRender
                ? images?.[1]?.width
                : images?.[0]?.width,
              imgHeight: conditionalRender
                ? images?.[1]?.height
                : images?.[0]?.height,
              value: 20,
            })
          }
          imageData={conditionalRender ? images?.[1] : images?.[0]}
        />
        <ImagePiece
          resourceId={
            conditionalRender
              ? images?.[2]?.previewImgResourceId || images?.[2]?.resourceId
              : images?.[1]?.previewImgResourceId || images?.[1]?.resourceId
          }
          index={conditionalRender ? 2 : 1}
          xsCol={12}
          mdCol={12}
          cssType={"height-two"}
          minHeightLoading={"min-h-[200px]"}
          openModal={openModal}
          styleImg={{
            minHeight: "5vw",
            maxHeight: "380px",
            objectFit: "cover",
            transform: "translateZ(0.1px)",
            objectPosition: "center",
          }}
          height={height}
          loadedImage={loadedImage}
          handleImageError={handleImageError}
          renderOverlay={renderOverlay}
          bg={bg}
          styleLoadingDiv={() =>
            calculateCssLoadingDiv({
              type: "height",
              imgWidth: conditionalRender
                ? images?.[2]?.width
                : images?.[1]?.width,
              imgHeight: conditionalRender
                ? images?.[2]?.height
                : images?.[1]?.height,
              value: 20,
            })
          }
          imageData={conditionalRender ? images?.[2] : images?.[1]}
        />
      </Row>
    );
  };

  const renderThree = () => {
    const conditionalRender =
      images?.length === 4 || (images?.length > +countFrom && +countFrom === 4);

    const overlay =
      !countFrom ||
      countFrom > 5 ||
      (images?.length > countFrom && [4, 5].includes(+countFrom))
        ? renderCountOverlay(true)
        : renderOverlay(conditionalRender ? 3 : 4);

    if (!isImageLoaded) {
      return <div>Loading...</div>;
    }

    return (
      <Row>
        <ImagePiece
          resourceId={
            conditionalRender
              ? images?.[1]?.previewImgResourceId || images?.[1]?.resourceId
              : images?.[2]?.previewImgResourceId || images?.[2]?.resourceId
          }
          index={conditionalRender ? 1 : 2}
          xsCol={12}
          mdCol={8}
          cssType={"height-three"}
          minHeightLoading={"min-h-[150px]"}
          styleImg={{
            minHeight: "5vw",
            maxHeight: "380px",
            objectFit: "cover",
            transform: "translateZ(0.1px)",
            objectPosition: "center",
          }}
          openModal={openModal}
          height={height}
          loadedImage={loadedImage}
          handleImageError={handleImageError}
          bg={bg}
          styleLoadingDiv={() =>
            calculateCssLoadingDiv({
              type: "height",
              imgWidth: conditionalRender
                ? images?.[1]?.width
                : images?.[2]?.width,
              imgHeight: conditionalRender
                ? images?.[1]?.height
                : images?.[2]?.height,
              value: 20,
            })
          }
          imageData={conditionalRender ? images?.[1] : images?.[2]}
        />
        <ImagePiece
          resourceId={
            conditionalRender
              ? images?.[2]?.previewImgResourceId || images?.[2]?.resourceId
              : images?.[3]?.previewImgResourceId || images?.[3]?.resourceId
          }
          index={conditionalRender ? 1 : 0}
          xsCol={12}
          mdCol={8}
          cssType={"height-three"}
          minHeightLoading={"min-h-[150px]"}
          openModal={openModal}
          height={height}
          styleImg={{
            minHeight: "5vw",
            maxHeight: "380px",
            objectFit: "cover",
            transform: "translateZ(0.1px)",
            objectPosition: "center",
          }}
          loadedImage={loadedImage}
          handleImageError={handleImageError}
          bg={bg}
          styleLoadingDiv={() =>
            calculateCssLoadingDiv({
              type: "height",
              imgWidth: conditionalRender
                ? images?.[2]?.width
                : images?.[3]?.width,
              imgHeight: conditionalRender
                ? images?.[2]?.height
                : images?.[3]?.height,
              value: 20,
            })
          }
          imageData={conditionalRender ? images?.[2] : images?.[3]}
        />
        <ImagePiece
          resourceId={
            conditionalRender
              ? images?.[3]?.previewImgResourceId || images?.[3]?.resourceId
              : images?.[4]?.previewImgResourceId || images?.[4]?.resourceId
          }
          index={conditionalRender ? 1 : 0}
          xsCol={12}
          mdCol={8}
          cssType={"height-three"}
          minHeightLoading={"min-h-[150px]"}
          openModal={openModal}
          height={height}
          styleImg={{
            minHeight: "5vw",
            maxHeight: "380px",
            objectFit: "cover",
            transform: "translateZ(0.1px)",
            objectPosition: "center",
          }}
          loadedImage={loadedImage}
          handleImageError={handleImageError}
          overlay={overlay}
          bg={bg}
          styleLoadingDiv={() =>
            calculateCssLoadingDiv({
              type: "height",
              imgWidth: conditionalRender
                ? images?.[3]?.width
                : images?.[4]?.width,
              imgHeight: conditionalRender
                ? images?.[3]?.height
                : images?.[4]?.height,
              value: 20,
            })
          }
          imageData={conditionalRender ? images?.[3] : images?.[4]}
        />
      </Row>
    );
  };

  const renderOverlay = (id) => {
    if (hideOverlay) {
      return false;
    }

    return [<div key={`cover-${id}`} className="cover slide"></div>];
  };

  const renderCountOverlay = (more) => {
    const extra = images.length - (countFrom && countFrom > 5 ? 5 : countFrom);

    return [
      more && <div key="count" className="cover"></div>,
      more && (
        <div
          onClick={() => openModal(4)}
          key="count-sub"
          className="cover-text w-full cursor-pointer text-[200%]"
        >
          +{extra}
        </div>
      ),
    ];
  };

  const calculateCssLoadingDiv = ({
    type = "width",
    imgWidth,
    imgHeight,
    value,
    minHeightVw = 5,
  } = {}) => {
    if (!imgWidth || !imgHeight) return null;

    const vwUnit = window.innerWidth / 100;
    const aspectRatio = imgWidth / imgHeight;
    const maxPx = value * vwUnit;
    const minHeightPx = minHeightVw * vwUnit;

    let widthPx, heightPx;

    if (type === "width") {
      if (imgWidth <= maxPx) {
        widthPx = imgWidth;
        heightPx = imgHeight;
      } else {
        widthPx = maxPx;
        heightPx = maxPx / aspectRatio;
      }
    } else {
      if (imgHeight <= maxPx) {
        heightPx = imgHeight;
        widthPx = imgWidth;
      } else {
        heightPx = maxPx;
        widthPx = maxPx * aspectRatio;
      }
    }

    if (heightPx < minHeightPx) {
      heightPx = minHeightPx;
      widthPx = heightPx * aspectRatio;
    }

    return {
      minWidth: `${widthPx / vwUnit}vw`,
      minHeight: `${heightPx / vwUnit}vw`,
    };
  };

  return (
    <div>
      {imagesToShow === 1 && (
        <Row>
          <ImagePiece
            resourceId={
              images?.[0]?.previewImgResourceId || images?.[0]?.resourceId
            }
            index={0}
            xsCol={24}
            mdCol={24}
            cssType={"height-two"}
            minHeightLoading={"min-h-[200px]"}
            openModal={openModal}
            height={height}
            loadedImage={loadedImage}
            handleImageError={handleImageError}
            bg={bg}
            isMention={isMention}
            content={content}
            styleImg={{
              minHeight: "5vw",
              maxHeight: "20vw",
              maxWidth: "20vw",
              objectFit: "cover",
              transform: "translateZ(0.1px)",
            }}
            imagesToShow={imagesToShow}
            styleLoadingDiv={() =>
              calculateCssLoadingDiv({
                type: "width",
                imgWidth: images?.[0]?.width,
                imgHeight: images?.[0]?.height,
                value: 20,
              })
            }
            imageData={images?.[0]}
          />
        </Row>
      )}
      {[3, 4].includes(imagesToShow) && (
        <Row>
          <ImagePiece
            resourceId={
              images?.[0]?.previewImgResourceId || images?.[0]?.resourceId
            }
            index={0}
            xsCol={24}
            mdCol={24}
            cssType={"height-two"}
            minHeightLoading={"min-h-[200px]"}
            openModal={openModal}
            height={height}
            styleImg={{
              minHeight: "5vw",
              maxHeight: "380px",
              objectFit: "cover",
              transform: "translateZ(0.1px)",
              objectPosition: "center",
            }}
            loadedImage={loadedImage}
            handleImageError={handleImageError}
            bg={bg}
            styleLoadingDiv={() =>
              calculateCssLoadingDiv({
                type: "height",
                imgWidth: images?.[0]?.width,
                imgHeight: images?.[0]?.height,
                value: 20,
              })
            }
            imageData={images?.[0]}
          />
        </Row>
      )}
      {imagesToShow >= 2 && imagesToShow !== 4 && renderTwo()}
      {imagesToShow >= 4 && renderThree()}

      {/* Modal viewer */}
      {resourceViewer?.visible && (
        <MediaViewerModal resourceViewer={resourceViewer} />
      )}
    </div>
  );
};

export default GridPhotos;
